import { useEffect, useState } from 'react';
import { QueryClient } from 'react-query';
import {
    AppSettings,
    AppSettingsDataClient,
    QUERY_KEYS,
    QUERY_KEYS_PERSIST_BLACKLIST,
} from '@24i/nxg-sdk-photon';
import { persistQueryClient } from 'react-query/persistQueryClient-experimental';
import { getServiceConfig } from '../../../utils/configuration';
import { DEFAULT_CACHE_TIME } from '../client';
import QueryPersistCacheInstance from '../queryPersistCacheInstance';

export const useRemoteConfiguration = (
    queryClient: QueryClient,
    appSettingsDataClient: AppSettingsDataClient
) => {
    const [isFinished, setIsFinished] = useState(false);

    const updateQueryClientConfiguration = async (configuration: AppSettings) => {
        const queryCachePersist = configuration?.features?.queryCachePersist;
        if (!queryCachePersist?.enabled) return;

        const expirationTime = queryCachePersist?.expirationTime ?? DEFAULT_CACHE_TIME;
        // update query client configuration
        queryClient.setDefaultOptions({
            queries: {
                staleTime: expirationTime, // im memory cache expiration time
                cacheTime: expirationTime, // persist cache expiration time
            },
        });

        // List of QUERY KEYS to persist
        let queryKeys = (configuration?.features?.queryCachePersist?.queryKeys ??
            Object.values(QUERY_KEYS)) as QUERY_KEYS[];
        queryKeys = queryKeys.filter((key) => !QUERY_KEYS_PERSIST_BLACKLIST.includes(key));

        // apply persist configuration to query client
        QueryPersistCacheInstance.init(queryKeys);
        const persistor = QueryPersistCacheInstance.getInstance()?.getPersistor();
        if (persistor) {
            await persistQueryClient({
                queryClient,
                persistor,
                maxAge: expirationTime,
            });
        }
    };

    useEffect(() => {
        if (!appSettingsDataClient) {
            setIsFinished(true);
            return;
        }

        setIsFinished(false);
        queryClient
            .fetchQuery({
                queryKey: QUERY_KEYS.config,
                queryFn: () => getServiceConfig(appSettingsDataClient),
            })
            .then((result) => updateQueryClientConfiguration(result))
            .finally(() => {
                setIsFinished(true);
            });
    }, []);

    return { isFinished };
};
